import { HttpResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import {
    ConfiguratorContactService,
    ConfiguratorDatastoreService,
    ContactDTO,
    DatastoreDto,
    Partner,
} from '@tj/tj-dmp-lib-api';
import { MenuItem, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'dmp-contact-edit',
    templateUrl: './contact-edit.component.html',
    styleUrls: ['./contact-edit.component.scss'],
    standalone: false
})
export class ContactEditComponent implements OnInit {

    id?: string;

    breadcrumb: MenuItem[] = [
        { label: this.translate.instant('menu.invoice.label') },
        { label: this.translate.instant('menu.invoice.contacts') },
        { label: this.translate.instant('invoicing.common.create') }
    ];
    description: string;

    civilities = [
        { name: this.translate.instant('invoicing.common.mr'), code: 'Mr' },
        { name: this.translate.instant('invoicing.common.ms'), code: 'Ms' },
    ];

    form: UntypedFormGroup;
    submitted = false;
    actual: ContactDTO;
    tenantCode: string;
    group: DatastoreDto;
    partner: Partner;
    isDisabled: boolean = false;
    initialPartnerCode: string | null = null;

    get useRouteSelection(): boolean {
        return !!this.id || !!this.initialPartnerCode;
    }

    constructor(private fb: UntypedFormBuilder,
        protected readonly route: ActivatedRoute,
        protected contactService: ConfiguratorContactService,
        protected datastoreResourceService: ConfiguratorDatastoreService,
        private messageService: MessageService,
        private router: Router,
        private translate: TranslateService
    ) {
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    async ngOnInit() {

        const id = this.route.snapshot.paramMap.get('id');
        this.id = id;
        this.tenantCode = this.route.snapshot.paramMap.get('tenant');
        this.initialPartnerCode = !id ? this.route.snapshot.queryParamMap?.get('partner')?.trim() || null : null;

        if (id) {
            this.description = this.translate.instant('invoicing.contacts.edit');
            this.breadcrumb[2].label = this.translate.instant('invoicing.common.edition');
            this.isDisabled = true;
            this.contactService
                .getContactUsingGET(this.tenantCode, id, 'response')
                .subscribe(
                    (res: HttpResponse<ContactDTO>) => {
                        this.actual = res.body;
                        this.initialPartnerCode = this.actual.partnerVo.code;
                        this.form = this.fb.group({
                            id: [this.actual.id],
                            tenant: [this.actual.datastoreVo.code, Validators.required],
                            partner: [this.actual.partnerVo.code, Validators.required],
                            civility: [this.civilities.find(civility => civility.code === this.actual.civility), Validators.required],
                            lastname: [this.actual.name, Validators.required],
                            firstname: [this.actual.firstname, Validators.required],
                            function: [this.actual.fonction],
                            email: [this.actual.email, [Validators.email, Validators.required]],
                            phone_number: [this.actual.phone, Validators.pattern('[- +()0-9]+')],
                            invoicing: [this.actual.invoicing],
                            company: [this.actual?.company
                                ? { label: this.actual.company, value: this.actual.company }
                                : null,
                            Validators.required]
                        });
                    })
        } else {

            this.description = this.translate.instant('invoicing.contacts.create');
            this.form = this.fb.group({
                id: [''],
                tenant: [this.tenantCode, Validators.required],
                partner: [this.initialPartnerCode || '', Validators.required],
                civility: [this.civilities[0], Validators.required],
                lastname: ['', Validators.required],
                firstname: ['', Validators.required],
                function: [''],
                email: ['', [Validators.email, Validators.required]],
                phone_number: ['', Validators.pattern('[- +()0-9]+')],
                invoicing: [this.route.snapshot.queryParamMap?.get('invoicing') === 'true'],
                company: ['', Validators.required]
            });
        }
    }

    onGroupChange(group: DatastoreDto) {
        this.group = group;
        this.partner = null;
        if (this.group) {
            this.form.patchValue({ tenant: group.code, partner: null });
        } else {
            this.form.patchValue({ tenant: null, partner: null });
        }
    }

    onPartnerChange(partner: Partner) {
        this.partner = partner;
        if (this.partner) {
            this.form.patchValue({ partner: this.partner.code });
        } else {
            this.form.patchValue({ partner: null });
        }
    }

    submit() {
        this.submitted = true;
        this.form.markAllAsTouched();
        this.form.updateValueAndValidity();

        if (this.form.invalid) {
            return;
        }

        if (this.actual) {
            this.contactService.updateContactUsingPUT(this.form.value.tenant, {
                id: this.form.value.id,
                civility: this.form.value.civility.code,
                name: this.form.value.lastname,
                firstname: this.form.value.firstname,
                fonction: this.form.value.function,
                email: this.form.value.email,
                phone: this.form.value.phone_number,
                partnerVo: { code: this.form.value.partner },
                datastoreVo: { code: this.form.value.tenant },
                company: this.form.value.company.value,
                invoicing: this.form.value.invoicing
            }).subscribe(() => {
                const navigationExtras: NavigationExtras = { state: { message: 'success_updated' } };
                this.router.navigate([`/h/invoicing/contact`], navigationExtras);
            }, (e) => {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('invoicing.common.error'), detail: this.translate.instant('invoicing.common.genericError', {error: e.toString()}) });
            });
        } else {
            this.contactService.createContactUsingPOST(this.form.value.tenant, {
                civility: this.form.value.civility.code,
                name: this.form.value.lastname,
                firstname: this.form.value.firstname,
                fonction: this.form.value.function,
                email: this.form.value.email,
                phone: this.form.value.phone_number,
                partnerVo: { code: this.form.value.partner },
                datastoreVo: { code: this.form.value.tenant },
                company: this.form.value.company.value,
                invoicing: this.form.value.invoicing
            }).subscribe(() => {
                const navigationExtras: NavigationExtras = { state: { message: 'success_created' } };
                this.router.navigate([`/h/invoicing/contact`], navigationExtras);
            }, (e) => {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('invoicing.common.error'), detail: this.translate.instant('invoicing.common.genericError', {error: e.toString()}) });
            });
        }
    }
}
